import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * SearchableSelect — a lightweight replacement for a native <select> when the
 * option list is long enough to warrant typing to filter.
 *
 * Props:
 *  - value: currently selected id (string) or ""
 *  - onChange(id): fires with the id of the picked option, or "" when cleared
 *  - options: [{value, label, sub}]  // label = main line, sub = optional grey subtitle
 *  - placeholder, disabled, testid
 */
export default function SearchableSelect({ value, onChange, options, placeholder = "— pick one —", disabled, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  const selected = useMemo(() => options.find(o => o.value === value) || null, [options, value]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter(o => (o.label || "").toLowerCase().includes(s) || (o.sub || "").toLowerCase().includes(s));
  }, [q, options]);

  useEffect(() => {
    if (!open) return;  // only wire listeners while the dropdown is open

    // Outside-click via capture phase — this is the important bit.
    // Several modals in the app do onMouseDown={e.stopPropagation()} on
    // their inner container to prevent backdrop dismissal, which also kills
    // a bubble-phase document listener. Capture phase fires on the way DOWN
    // from document to target, so nothing can stop it upstream. Result:
    // clicking anywhere outside the picker (including inside a modal that
    // stops propagation) reliably closes the dropdown.
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ("");
      }
    };
    // Escape closes without picking. Keydown NOT via capture — we want any
    // input.onKeyDown (e.g. Enter to submit a form) to still get its turn.
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (opt) => { onChange(opt.value); setOpen(false); setQ(""); };
  const clear = (e) => { e.stopPropagation(); onChange(""); setQ(""); };

  return (
    <div ref={wrapRef} style={{position:"relative"}} data-testid={testid}>
      <div
        onClick={() => !disabled && setOpen(true)}
        style={{
          border: "1px solid var(--ach-card-border, #ddd)",
          borderRadius: 6, padding: "8px 10px", fontSize: 13, cursor: disabled ? "not-allowed" : "text",
          background: disabled ? "#f4f4f6" : "#fff",
          display: "flex", alignItems: "center", gap: 8, minHeight: 36,
        }}
        data-testid={`${testid}-trigger`}
      >
        {selected ? (
          <div style={{flex:1, minWidth: 0}}>
            <div style={{fontWeight: 500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{selected.label}</div>
            {selected.sub && <div style={{fontSize: 11, color:"var(--ach-text-2)"}}>{selected.sub}</div>}
          </div>
        ) : (
          <div style={{flex:1, color:"var(--ach-text-2)"}}>{placeholder}</div>
        )}
        {selected && !disabled && (
          <button type="button" onClick={clear} data-testid={`${testid}-clear`}
            style={{background:"none", border:"none", cursor:"pointer", color:"var(--ach-text-2)", fontSize:16, padding:"0 4px"}}>×</button>
        )}
        <span style={{color:"var(--ach-text-2)", fontSize:11}}>▾</span>
      </div>
      {open && !disabled && (
        <div
          style={{
            position:"absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 1000,
            background:"#fff", border:"1px solid var(--ach-card-border, #ddd)", borderRadius: 6,
            boxShadow:"0 4px 16px rgba(0,0,0,0.08)", maxHeight: 320, overflow:"hidden", display:"flex", flexDirection:"column",
          }}
        >
          <input
            autoFocus
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            placeholder="Type to filter…"
            style={{border:"none", borderBottom:"1px solid var(--ach-row-divider,#eee)", padding:"8px 10px", fontSize:13, outline:"none"}}
          />
          <div style={{overflowY:"auto", flex:1}} data-testid={`${testid}-options`}>
            {filtered.length === 0 ? (
              <div style={{padding:12, textAlign:"center", fontSize:12, color:"var(--ach-text-2)"}}>No matches</div>
            ) : filtered.map(opt => (
              <div key={opt.value}
                data-testid={`${testid}-opt-${opt.value}`}
                onClick={()=>pick(opt)}
                style={{
                  padding:"8px 10px", cursor:"pointer", fontSize:13,
                  borderBottom:"1px solid var(--ach-row-divider,#f2f2f2)",
                  background: opt.value === value ? "var(--ach-page-bg,#faf9fb)" : "#fff",
                }}
                onMouseEnter={(e)=>e.currentTarget.style.background="var(--ach-page-bg,#faf9fb)"}
                onMouseLeave={(e)=>e.currentTarget.style.background = opt.value === value ? "var(--ach-page-bg,#faf9fb)" : "#fff"}>
                <div style={{fontWeight:500}}>{opt.label}</div>
                {opt.sub && <div style={{fontSize:11, color:"var(--ach-text-2)"}}>{opt.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
